import {
  Button,
  ButtonVariant,
  Card,
  Drawer,
  Modal,
  Tooltip,
} from "@merge-api/merge-javascript-shared";
import SucceededDiffCardContent from "./components/SucceededDiffCardContent";
import {
  CommonModelDiffRunExecution,
  CommonModelDiffRunStatus,
  ViewExecutionLogsHandler,
} from "./entities";
import AwaitingCompletionDiffCardContent from "./components/AwaitingCompletionDiffCardContent";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, X } from "lucide-react";
import CommonModelDiffDrawer from "./components/CommonModelDiffDrawer";
import FailedDiffAlert from "./components/FailedDiffAlert";

// 3-diagonal-line handle browsers render for a resizable <textarea>
const ResizeGripIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path
      d="M13 3L3 13M13 7.5L7.5 13M13 12L12 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const POPUP_MIN_WIDTH = 640;
const POPUP_MIN_HEIGHT = 400;
const POPUP_DEFAULT_WIDTH_RATIO = 0.9;
const POPUP_DEFAULT_HEIGHT_RATIO = 0.85;
const POPUP_MAX_WIDTH_RATIO = 0.98;
const POPUP_MAX_HEIGHT_RATIO = 0.95;

// per-axis growth direction: -1/1 = grows when dragged that way, 0 = axis unaffected
type ResizeDirection = { dx: -1 | 0 | 1; dy: -1 | 0 | 1 };

// invisible drag strips for the other 3 edges + 3 corners; bottom-right gets its own visible handle
const RESIZE_EDGE_HANDLES: { direction: ResizeDirection; className: string }[] = [
  { direction: { dx: 0, dy: -1 }, className: "-top-1 left-4 right-4 h-3 cursor-ns-resize" },
  { direction: { dx: 0, dy: 1 }, className: "-bottom-1 left-4 right-4 h-3 cursor-ns-resize" },
  { direction: { dx: -1, dy: 0 }, className: "-left-1 top-4 bottom-4 w-3 cursor-ew-resize" },
  { direction: { dx: 1, dy: 0 }, className: "-right-1 top-4 bottom-4 w-3 cursor-ew-resize" },
  { direction: { dx: -1, dy: -1 }, className: "-top-1 -left-1 h-4 w-4 cursor-nwse-resize" },
  { direction: { dx: 1, dy: -1 }, className: "-top-1 -right-1 h-4 w-4 cursor-nesw-resize" },
  { direction: { dx: -1, dy: 1 }, className: "-bottom-1 -left-1 h-4 w-4 cursor-nesw-resize" },
];
interface CommonModelDiffRunResultProps {
  candidateTitle?: string;
  diffRun: CommonModelDiffRunExecution;
  integrationID: string;
  setCommonModelDiffRunExecutions: (diffRuns: CommonModelDiffRunExecution[]) => void;
  commonModelDiffRunExecutions: CommonModelDiffRunExecution[];
  onViewExecutionLogs?: ViewExecutionLogsHandler;
}

const STATUS_STYLES = {
  [CommonModelDiffRunStatus.PENDING]: "bg-gray-0",
  [CommonModelDiffRunStatus.IN_PROGRESS]: "bg-gray-0",
  [CommonModelDiffRunStatus.SUCCEEDED]: "bg-white cursor-pointer hover:bg-gray-0",
  [CommonModelDiffRunStatus.FAILED]: "bg-red-0",
  [CommonModelDiffRunStatus.ABORTED]: "bg-red-0",
};

const CommonModelDiffRunResult = ({
  candidateTitle = "Staged",
  diffRun,
  integrationID,
  setCommonModelDiffRunExecutions,
  commonModelDiffRunExecutions,
  onViewExecutionLogs,
}: CommonModelDiffRunResultProps) => {
  // state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // when true, the comparison is shown in a large centered popup instead of the drawer
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // popup size in px; null until the user drags the resize handle, then sticky
  const [popupSize, setPopupSize] = useState<{ width: number; height: number } | null>(null);
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    direction: ResizeDirection;
  } | null>(null);

  const getDefaultPopupSize = () => ({
    width: Math.round(window.innerWidth * POPUP_DEFAULT_WIDTH_RATIO),
    height: Math.round(window.innerHeight * POPUP_DEFAULT_HEIGHT_RATIO),
  });

  // native CSS resize is unreliable in this centered flex layout, so drag by hand instead
  const handleResizeMouseDown = useCallback(
    (direction: ResizeDirection) => (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      const current = popupSize ?? getDefaultPopupSize();
      dragStateRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        startWidth: current.width,
        startHeight: current.height,
        direction,
      };
      setPopupSize(current);
    },
    [popupSize]
  );

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const { startX, startY, startWidth, startHeight, direction } = dragState;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      const maxWidth = window.innerWidth * POPUP_MAX_WIDTH_RATIO;
      const maxHeight = window.innerHeight * POPUP_MAX_HEIGHT_RATIO;
      setPopupSize({
        width:
          direction.dx === 0
            ? startWidth
            : Math.min(maxWidth, Math.max(POPUP_MIN_WIDTH, startWidth + direction.dx * dx * 2)),
        height:
          direction.dy === 0
            ? startHeight
            : Math.min(maxHeight, Math.max(POPUP_MIN_HEIGHT, startHeight + direction.dy * dy * 2)),
      });
    };
    const handleMouseUp = () => {
      dragStateRef.current = null;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const effectivePopupSize = popupSize ?? getDefaultPopupSize();

  if (diffRun.status === CommonModelDiffRunStatus.ABORTED) {
    return null;
  }

  const isFailed = diffRun.status === CommonModelDiffRunStatus.FAILED;
  const isSucceeded = diffRun.status === CommonModelDiffRunStatus.SUCCEEDED;
  const isAwaitingCompletion = [
    CommonModelDiffRunStatus.PENDING,
    CommonModelDiffRunStatus.IN_PROGRESS,
  ].includes(diffRun.status);

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  // fully closes the comparison, whether it's currently shown in the drawer or the popup
  const handlePopupClose = () => {
    setIsDrawerOpen(false);
    setIsPopupOpen(false);
  };

  return (
    <>
      {isFailed ? (
        <FailedDiffAlert
          diffRun={diffRun}
          setCommonModelDiffRunExecutions={setCommonModelDiffRunExecutions}
          commonModelDiffRunExecutions={commonModelDiffRunExecutions}
          onViewExecutionLogs={onViewExecutionLogs}
        />
      ) : (
        <Card
          variant="outline"
          className={clsx("p-5", STATUS_STYLES[diffRun.status])}
          onClick={() => {
            if (isSucceeded) {
              setIsDrawerOpen(true);
            }
          }}
          type={isSucceeded ? "button" : "div"}
        >
          {isSucceeded && <SucceededDiffCardContent diffRun={diffRun} />}
          {isAwaitingCompletion && (
            <AwaitingCompletionDiffCardContent
              diffRun={diffRun}
              setCommonModelDiffRunExecutions={setCommonModelDiffRunExecutions}
              commonModelDiffRunExecutions={commonModelDiffRunExecutions}
              onViewExecutionLogs={onViewExecutionLogs}
            />
          )}
        </Card>
      )}
      <Drawer
        open={isDrawerOpen && !isPopupOpen}
        onClose={handleDrawerClose}
        title=""
        titlePrefix={<SucceededDiffCardContent diffRun={diffRun} isDrawerPrefix={true} />}
        anchor="right"
        exitPrefix={
          <Tooltip title="Open in a larger popup">
            <Button
              size="sm"
              variant={ButtonVariant.IconShadowHidden}
              ariaLabel="Open in a larger popup"
              onClick={() => setIsPopupOpen(true)}
            >
              <Maximize2 size={16} className="text-gray-40" />
            </Button>
          </Tooltip>
        }
      >
        <CommonModelDiffDrawer
          candidateTitle={candidateTitle}
          diffRun={diffRun}
          integrationID={integrationID}
        />
      </Drawer>
      <Modal open={isDrawerOpen && isPopupOpen} onClose={handlePopupClose} animate>
        <div
          className="relative"
          style={{ width: effectivePopupSize.width, height: effectivePopupSize.height }}
        >
          <Card variant="none" size="xl" className="flex h-full w-full flex-col overflow-hidden">
            <div className="flex items-start justify-between p-5">
              <SucceededDiffCardContent diffRun={diffRun} isDrawerPrefix={true} />
              <div className="flex shrink-0 items-center space-x-1">
                <Tooltip title="Collapse to drawer view">
                  <Button
                    size="sm"
                    variant={ButtonVariant.IconShadowHidden}
                    ariaLabel="Collapse to drawer view"
                    onClick={() => setIsPopupOpen(false)}
                  >
                    <Minimize2 size={16} className="text-gray-40" />
                  </Button>
                </Tooltip>
                <Button
                  size="sm"
                  variant={ButtonVariant.IconShadowHidden}
                  ariaLabel="Close"
                  onClick={handlePopupClose}
                >
                  <X size={16} className="text-gray-40" />
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <CommonModelDiffDrawer
                candidateTitle={candidateTitle}
                diffRun={diffRun}
                integrationID={integrationID}
              />
            </div>
          </Card>
          {RESIZE_EDGE_HANDLES.map(({ direction, className }, i) => (
            <div
              key={i}
              onMouseDown={handleResizeMouseDown(direction)}
              className={clsx("absolute", className)}
            />
          ))}
          <Tooltip title="Drag to resize">
            <div
              onMouseDown={handleResizeMouseDown({ dx: 1, dy: 1 })}
              className="absolute -bottom-1 -right-1 flex h-9 w-9 cursor-nwse-resize items-center justify-center text-gray-40 hover:text-gray-60"
            >
              <ResizeGripIcon size={20} />
            </div>
          </Tooltip>
        </div>
      </Modal>
    </>
  );
};

export default CommonModelDiffRunResult;
